import React from 'react';
import { Alert, AlertTitle, IconButton, Portal, Stack, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { keyframes } from '@mui/system';
import { lighten } from '@mui/material/styles';

const dismissCountdown = keyframes`
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const NotificationProvider: React.FC = () => {
  const { notifications, removeNotification } = useNotificationStore();

  return (
    <Portal>
      <Stack
        role="status"
        aria-live="polite"
        aria-relevant="additions removals"
        spacing={1.5}
        sx={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 1400,
          maxWidth: 400,
        }}
      >
        {notifications.map((notification) => {
          const showCountdown =
            !notification.persistent &&
            typeof notification.duration === 'number' &&
            notification.duration > 0;

          return (
            <Alert
              key={notification.id}
              severity={notification.type}
              variant="filled"
              action={
                notification.action ?? (
                  <IconButton
                    size="small"
                    color="inherit"
                    aria-label="Dismiss notification"
                    onClick={() => removeNotification(notification.id)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                )
              }
              onClose={() => removeNotification(notification.id)}
              sx={{
                alignItems: 'flex-start',
                position: 'relative',
                overflow: 'hidden',
                animation: `${slideInRight} 0.3s ease-out`,
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 1px 4px rgba(0,0,0,0.1)',
                '& a': {
                  color: (theme) => lighten(theme.palette.info.light, 0.6),
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textDecorationThickness: '2px',
                  textUnderlineOffset: 3,
                },
              }}
            >
              <AlertTitle>{notification.title}</AlertTitle>
              {notification.message}
              {showCountdown ? (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    backgroundColor: 'rgba(255,255,255,0.75)',
                    transformOrigin: 'right',
                    animation: `${dismissCountdown} ${notification.duration}ms linear forwards`,
                  }}
                />
              ) : null}
            </Alert>
          );
        })}
      </Stack>
    </Portal>
  );
};
